import { Currency, currencyFromApi } from '../pricing';
import {
  SalesInvoiceInterface,
  PaymentStatuses,
  SalesInvoiceStatus as InternalSalesInvoiceStatus,
  SalesInvoiceType as InternalSalesInvoiceType,
  ListSalesInvoiceRequestListSalesInvoiceFilters,
} from '@vendasta/billing';

export interface SalesInvoice {
  invoiceId: string;
  externalId: string;
  invoiceNumber: string;
  merchantId: string;
  invoiceDate?: Date;
  startDate?: Date;
  endDate?: Date;
  dueDate?: Date;
  currency: Currency;
  status: SalesInvoiceStatus;
  type?: SalesInvoiceType;
  total: number;
  paymentStatus?: string;
  outstanding?: number;
}

export enum SalesInvoiceStatus {
  InProgress = 'IN_PROGRESS',
  ReadyToPost = 'READY_TO_POST',
  Complete = 'COMPLETE',
  Discarded = 'DISCARDED',
}

export enum SalesInvoiceType {
  Instant = 'INSTANT',
  Invoiced = 'INVOICED',
  Unset = 'UNSET',
}

export enum PaymentStatusType {
  Not_Applicable = 'Not Applicable',
  Unpaid = 'Unpaid',
  Paid = 'Paid',
  Part_Paid = 'Part Paid',
}

export interface ListSalesInvoicesFilters {
  merchantId?: string;
  statuses?: SalesInvoiceStatus[];
  paymentStatuses?: PaymentStatusType[];
  invoiceId?: string[];
}

export function salesInvoiceFromApi(siApi: SalesInvoiceInterface): SalesInvoice {
  return {
    invoiceId: siApi.invoiceId || '',
    externalId: siApi.externalId || '',
    invoiceNumber: siApi.invoiceNumber || '',
    merchantId: siApi.merchantId || '',
    invoiceDate: siApi.invoiceDate ? new Date(siApi.invoiceDate) : undefined,
    startDate: siApi.startDate ? new Date(siApi.startDate) : undefined,
    endDate: siApi.endDate ? new Date(siApi.endDate) : undefined,
    dueDate: siApi.dueDate ? new Date(siApi.dueDate) : undefined,
    currency: currencyFromApi(siApi.currency),
    status: salesInvoiceStatusFromApi(siApi.status),
    type: salesInvoiceTypeFromApi(siApi.type),
    total: siApi.total || 0,
    paymentStatus: siApi.paymentStatus,
    outstanding: siApi.outstanding || 0,
  };
}

export function listSalesInvoiceFiltersToApi(
  filters: ListSalesInvoicesFilters,
): ListSalesInvoiceRequestListSalesInvoiceFilters {
  return new ListSalesInvoiceRequestListSalesInvoiceFilters({
    ...filters,
    statuses: (filters.statuses || []).map((s) => salesInvoiceStatusToApi(s)),
    paymentStatuses: (filters.paymentStatuses || []).map((s) => salesInvoicePaymentStatusToApi(s)),
  });
}

function salesInvoiceStatusFromApi(sApi: InternalSalesInvoiceStatus | undefined): SalesInvoiceStatus {
  switch (sApi) {
    case InternalSalesInvoiceStatus.SALES_INVOICE_STATUS_READY_TO_POST:
      return SalesInvoiceStatus.ReadyToPost;
    case InternalSalesInvoiceStatus.SALES_INVOICE_STATUS_COMPLETE:
      return SalesInvoiceStatus.Complete;
    case InternalSalesInvoiceStatus.SALES_INVOICE_STATUS_DISCARDED:
      return SalesInvoiceStatus.Discarded;
  }
  return SalesInvoiceStatus.InProgress;
}

function salesInvoiceStatusToApi(status: SalesInvoiceStatus | undefined): InternalSalesInvoiceStatus {
  switch (status) {
    case SalesInvoiceStatus.ReadyToPost:
      return InternalSalesInvoiceStatus.SALES_INVOICE_STATUS_READY_TO_POST;
    case SalesInvoiceStatus.Complete:
      return InternalSalesInvoiceStatus.SALES_INVOICE_STATUS_COMPLETE;
    case SalesInvoiceStatus.Discarded:
      return InternalSalesInvoiceStatus.SALES_INVOICE_STATUS_DISCARDED;
  }
  return InternalSalesInvoiceStatus.SALES_INVOICE_STATUS_IN_PROGRESS;
}

function salesInvoiceTypeFromApi(type: InternalSalesInvoiceType | undefined): SalesInvoiceType | undefined {
  switch (type) {
    case InternalSalesInvoiceType.SALES_INVOICE_TYPE_INVOICED:
      return SalesInvoiceType.Invoiced;
    case InternalSalesInvoiceType.SALES_INVOICE_TYPE_INSTANT:
      return SalesInvoiceType.Instant;
    default:
      return undefined;
  }
}

function salesInvoicePaymentStatusToApi(status: PaymentStatusType): PaymentStatuses {
  switch (status) {
    case PaymentStatusType.Paid:
      return PaymentStatuses.PAYMENT_STATUSES_PAID;
    case PaymentStatusType.Part_Paid:
      return PaymentStatuses.PAYMENT_STATUSES_PART_PAID;
    case PaymentStatusType.Unpaid:
      return PaymentStatuses.PAYMENT_STATUSES_UNPAID;
    case PaymentStatusType.Not_Applicable:
      return PaymentStatuses.PAYMENT_STATUSES_NOT_APPLICABLE;
  }
}
